import React, { ReactElement, useEffect } from "react";
import Routes from "../../Routes";
import { Navigation } from "../../components";
import Footer from "../../components/footer/Footer";
import { CreateTicketProvider } from "../../contexts/CreateTicketContext";
import { KeyboardShortcutProvider } from "../../contexts/KeyboardShortcutContext";
import { NotificationProvider } from "../../contexts/NotificationContext";
import usePortal, { PortalProvider } from "../../contexts/PortalContext";
import { PortalSubscriptionProvider } from "../../contexts/PortalSubscriptionContext";
import useCurrentSession, { AuthProvider } from "../../contexts/UserContext";
import { LoggingHandler, LoggingProvider, createContextData } from "../../contexts/logging/LoggingContext";
import { UpdatesProvider } from "../../contexts/updates/UpdatesContext";
import { ErrorDefault } from "../error/Error";
import { HelpProvider } from "../../contexts/HelpContext";

import "./App.css";
import Banner from "../../components/banner/Banner";

interface State {
    error?: Error
}

function ShowMain() {
    const { isLoggedIn } = useCurrentSession();

    const [hasPreload, setHasPreload] = React.useState(true);

    useEffect(() => {
        setTimeout(() => {
            setHasPreload(false);
        }, 500);
    }, []);

    if (isLoggedIn) {
        return (
            <div className={`app d-flex flex-column flex-lg-row overflow-hidden ${hasPreload ? "preload" : ""}`}>

                <Navigation shouldDisplayOnSide />
                <div className="flex-grow-1 overflow-auto d-flex flex-column app-container align-items-stretch">
                    <Banner />
                    <div className="flex-grow-1 ">
                        <Routes />
                    </div>
                    <Footer />
                </div>
            </div>
        );
    }
    return (
        <div className="app d-flex flex-column">
            <Navigation shouldDisplayOnSide={false} />
            <div className="flex-grow-1 overflow-auto d-grid app-container">
                <Routes />
            </div>
            <Footer />
        </div>
    );
}
export default class App extends React.Component<unknown, State> {
    private handleRejection: (event: PromiseRejectionEvent) => void;

    private handleError: (event: ErrorEvent) => void;

    constructor(props: unknown) {
        super(props);
        this.state = { };
        this.handleRejection = event => {
            LoggingHandler.instance.error(event.reason as Error, createContextData("error-handler"), "Uncaught promise");
            event.preventDefault();
        };
        this.handleError = event => {
            const error = typeof event === "string" ? new Error(event) : event.error;
            LoggingHandler.instance.error(error, createContextData("error-handler"), "Uncaught error");
            event.preventDefault();
        };
    }

    componentDidMount(): void {
        window.addEventListener("unhandledrejection", this.handleRejection);
        window.addEventListener("error", this.handleError);
    }

    componentDidCatch(error: Error): void {
        LoggingHandler.instance.error(error, createContextData("error-handler"), "Uncaught react error");
        this.setState({ error });
    }

    componentWillUnmount(): void {
        window.removeEventListener("unhandledrejection", this.handleRejection);
        window.removeEventListener("error", this.handleError);
    }

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    render(): ReactElement {
        return (
            <LoggingProvider>
                <HelpProvider>
                    {this.state.error ? (
                        <div className="app d-flex flex-column">
                            <Navigation />
                            <ErrorDefault message={this.state.error.message} stacktrace={this.state.error.stack?.split("\n")} />
                            <Footer />
                        </div>
                    ) : (

                        <PortalProvider>
                            <AuthProvider>
                                <KeyboardShortcutProvider>
                                    <NotificationProvider>
                                        <PortalSubscriptionProvider>
                                            <CreateTicketProvider>
                                                <UpdatesProvider>
                                                    <ShowMain />
                                                </UpdatesProvider>
                                            </CreateTicketProvider>
                                        </PortalSubscriptionProvider>
                                    </NotificationProvider>
                                </KeyboardShortcutProvider>
                            </AuthProvider>
                        </PortalProvider>

                    )}
                </HelpProvider>
            </LoggingProvider>
        );
    }
}
